import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedList, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { errorMessageFromCodeMap, unknownError } from './constants';
import messages from './messages';
import style from './style.module.less';
import { ConfirmDialogueModal } from '../../components/modals/confirm-dialogue-modal';
import { bulkloadSalesOrders } from '../../store/bulkload/bulkload.actions';
import Global from '../../store/global-selectors';
import { unlinkFulfillmentPlan } from '../fulfillment-plans.api';

const getErrorMessage = (errorCode) => {
  const errorMessage = errorMessageFromCodeMap[errorCode] ?? errorMessageFromCodeMap[unknownError];
  return errorMessage;
};

const UnlinkFulfillmentPlanConfirmationModal = ({ fulfillmentPlanId, onClose, salesOrderId, salesOrdersFulfilledIds }) => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [errorCode, setErrorCode] = useAsyncSafeState();
  const orderNumber = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'orderNumber',
    }),
  );
  const survivingOrderNumbers = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByIds(state, {
      salesOrderIds: salesOrdersFulfilledIds,
    }),
  ).reduce((acc, so) => {
    if (so.salesOrderId !== salesOrderId) {
      acc.push(so.orderNumber);
    }
    return acc;
  }, []);

  const onUnlink = useCallback(async () => {
    setClickCount((count) => count + 1);
    setIsProcessing(true);

    try {
      await unlinkFulfillmentPlan(fulfillmentPlanId, salesOrderId);
      dispatch(bulkloadSalesOrders(salesOrdersFulfilledIds));
      onClose();
    } catch (err) {
      const code = err.Errors?.[0]?.Code ?? unknownError;
      setErrorCode(code);
      setIsProcessing(false);
    }
  }, [dispatch, fulfillmentPlanId, onClose, salesOrderId, salesOrdersFulfilledIds, setErrorCode]);

  const body = (
    <>
      <FormattedMessage
        {...messages.unlinkModalBody}
        values={{
          orderNumber,
          survivingOrderNumbers: <FormattedList type='conjunction' value={survivingOrderNumbers} />,
        }}
      />
      {!!errorCode && (
        <BannerMessageV2
          className={style['unlink-modal-error-banner']}
          content={<FormattedMessage {...getErrorMessage(errorCode)} />}
          status={BannerStatus.Error}
        />
      )}
    </>
  );

  return (
    <ConfirmDialogueModal
      body={body}
      buttonDisabled={isProcessing || clickCount > 3}
      buttonMessage={<FormattedMessage {...messages.unlinkButton} />}
      buttonTheme='danger'
      header={<FormattedMessage {...messages.unlinkModalHeader} />}
      onClose={onClose}
      onConfirm={onUnlink}
    />
  );
};

UnlinkFulfillmentPlanConfirmationModal.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  salesOrderId: PropTypes.string.isRequired,
  salesOrdersFulfilledIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export { UnlinkFulfillmentPlanConfirmationModal };
